import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  CheckCircle, 
  Loader2, 
  Calendar, 
  ShieldCheck, 
  Clock, 
  ChevronRight,
  Sprout,
  Grid,
  Scissors,
  HelpCircle
} from 'lucide-react';
import { fallbackServices, contactInfo } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const iconMap = {
  Sprout: Sprout,
  Grid: Grid,
  Scissors: Scissors
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic SEO Metadata
  const serviceTitle = service ? (service.title?.da || service.title) : '';
  const seoTitle = serviceTitle ? `${serviceTitle} | BP Anlægsgartner` : 'Ydelse | BP Anlægsgartner';

  const getMetaDescription = () => {
    if (!service) return 'Læs mere om vores professionelle anlægsgartner-ydelser i Thy & Vestjylland. Få et uforpligtende tilbud i dag!';
    const rawDesc = service.description?.da || service.description || '';
    const cleanDesc = rawDesc.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    const maxLength = 110;
    let truncated = cleanDesc;
    if (cleanDesc.length > maxLength) {
      truncated = cleanDesc.substring(0, maxLength).trim() + '...';
    }
    const cta = ' Få et uforpligtende tilbud fra din lokale anlægsgartner i dag!';
    return (truncated + cta).substring(0, 160);
  };

  useDocumentMetadata(seoTitle, getMetaDescription());

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch all services to find the one with matching slug and to populate "other services"
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        let allServices = [];

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            allServices = data;
          } else {
            allServices = fallbackServices;
          }
        } else {
          allServices = fallbackServices;
        }

        // Find the current service by slug
        const currentService = allServices.find(s => {
          const serviceSlug = typeof s.slug === 'object' ? s.slug.da : s.slug;
          return serviceSlug === slug;
        });

        if (currentService) {
          setService(currentService);
          // Filter out current service for the sidebar
          const filtered = allServices.filter(s => {
            const serviceSlug = typeof s.slug === 'object' ? s.slug.da : s.slug;
            return serviceSlug !== slug;
          });
          setOtherServices(filtered);
        } else {
          throw new Error('Ydelsen blev ikke fundet');
        }
      } catch (err) {
        console.error('Fejl ved hentning af ydelse:', err);
        setError(err.message || 'Der opstod en fejl under hentning af ydelsen.');
      } finally {
        setLoading(false);
      }
    };

    fetchServiceData();
  }, [slug]);

  // Scroll to top on mount or slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: null });

    const serviceTitle = service 
      ? (typeof service.title === 'object' ? service.title.da : service.title)
      : '';

    const payload = {
      ...formData,
      subject: `Forespørgsel vedr. ${serviceTitle}`,
      source_service: slug
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error('Kunne ikke indsende formularen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en netværksfejl. Prøv venligst igen.'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center text-white">
        <Loader2 className="h-12 w-12 text-emerald-500 animate-spin mb-4" />
        <p className="text-stone-400 font-medium">Henter detaljer for ydelsen...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center text-white px-4">
        <div className="max-w-md w-full bg-stone-900 border border-stone-800 rounded-2xl p-8 text-center shadow-xl">
          <div className="h-12 w-12 rounded-full bg-red-950 text-red-400 flex items-center justify-center mx-auto mb-4 border border-red-900/50">
            <HelpCircle className="h-6 w-6" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Ydelsen blev ikke fundet</h3>
          <p className="text-stone-400 text-sm mb-6">
            Vi kunne desværre ikke finde den ydelse, du leder efter. Den er muligvis blevet flyttet eller slettet.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-xl transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  const titleText = typeof service.title === 'object' ? service.title.da : service.title;
  const descriptionHtml = typeof service.description === 'object' ? service.description.da : service.description;
  const imageUrl = Array.isArray(service.image) ? service.image[0] : service.image;
  const IconComponent = iconMap[service.icon_name] || HelpCircle;

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 pb-16 selection:bg-emerald-600 selection:text-white">
      
      {/* Hero Header */}
      <div className="relative bg-emerald-950 pt-44 pb-16 sm:pt-48 md:pt-56 md:pb-24 lg:pt-64 overflow-hidden border-b border-emerald-900/40">
        {/* Decorative background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-500 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-emerald-600 blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              {/* Breadcrumbs */}
              <div className="flex items-center gap-2 text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                <Link to="/" className="hover:text-white transition-colors">Forside</Link>
                <span>/</span>
                <span className="text-white/60">Ydelser</span>
                <span>/</span>
                <span className="text-white">{titleText}</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
                {titleText}
              </h1>
            </div>
            
            <div />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Left Column: Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Service Image */}
            <ScrollReveal variant="fade-up" duration={800}>
              <div className="relative h-[300px] sm:h-[450px] rounded-2xl overflow-hidden shadow-md border border-stone-200 bg-stone-100">
                <img 
                  src={imageUrl} 
                  alt={titleText}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/30 via-transparent to-transparent" />
              </div>
            </ScrollReveal>

            {/* Service Description */}
            <ScrollReveal variant="fade-up" duration={800} delay={100}>
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
                <h2 className="text-xl sm:text-2xl font-extrabold text-stone-900 mb-6 uppercase tracking-wide border-b border-stone-100 pb-4">
                  Om ydelsen
                </h2>
                <div 
                  className="prose prose-stone max-w-none text-stone-600 leading-relaxed text-sm sm:text-base space-y-4"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              </div>
            </ScrollReveal>

            {/* Quality Guarantees */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ScrollReveal variant="fade-up" duration={600} delay={150} className="h-full">
                <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex items-start gap-3 h-full">
                  <div className="text-stone-900">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">Fuldt forsikret</h4>
                    <p className="text-xs text-stone-500 mt-1">Din tryghed er altid i højsædet hos os.</p>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fade-up" duration={600} delay={250} className="h-full">
                <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex items-start gap-3 h-full">
                  <div className="text-stone-900">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">Aftaler overholdes</h4>
                    <p className="text-xs text-stone-500 mt-1">Vi kommer til tiden og leverer som aftalt.</p>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fade-up" duration={600} delay={350} className="h-full">
                <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex items-start gap-3 h-full">
                  <div className="text-stone-900">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">Fleksibel service</h4>
                    <p className="text-xs text-stone-500 mt-1">Vi tilpasser arbejdet efter dine ønsker.</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Contact Form specifically for this service */}
            <ScrollReveal variant="scale-up" duration={800} delay={200}>
              <div className="bg-emerald-950 text-white rounded-2xl border border-white p-6 sm:p-8 shadow-lg relative overflow-hidden">
                {/* Decorative background glow */}
                <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-emerald-800 rounded-full blur-3xl opacity-30 pointer-events-none" />
                
                <h3 className="text-xl font-extrabold text-white mb-2 uppercase tracking-wide relative z-10">
                  Få et uforpligtende tilbud på {titleText}
                </h3>
                <p className="text-white/90 text-sm mb-6 relative z-10">
                  Udfyld formularen herunder, så vender jeg hurtigt tilbage med en skarp pris og professionel rådgivning om dit projekt.
                </p>

                {formStatus.success ? (
                  <div className="bg-emerald-900/40 border border-emerald-800 rounded-xl p-6 text-center relative z-10">
                    <div className="h-12 w-12 rounded-full bg-emerald-800 text-emerald-300 flex items-center justify-center mx-auto mb-3 border border-emerald-700/50">
                      <CheckCircle className="h-6 w-6" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-1">Tak for din henvendelse!</h4>
                    <p className="text-emerald-200 text-sm">
                      Jeg har modtaget din besked vedrørende {titleText} og vender tilbage til dig så hurtigt som muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
                    {/* Honeypot Spam Protection */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                      <input
                        id="website_url"
                        type="text"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-white mb-1.5">
                          Navn <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border !border-white focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white bg-transparent text-white text-sm transition-all placeholder:text-white/50"
                          placeholder="Dit fulde navn"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-white mb-1.5">
                          Telefonnummer <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border !border-white focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white bg-transparent text-white text-sm transition-all placeholder:text-white/50"
                          placeholder="F.eks. 12 34 56 78"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-white mb-1.5">
                        E-mailadresse <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border !border-white focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white bg-transparent text-white text-sm transition-all placeholder:text-white/50"
                        placeholder="din@email.dk"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-white mb-1.5">
                        Beskrivelse af dit projekt <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border !border-white focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white bg-transparent text-white text-sm transition-all resize-none placeholder:text-white/50"
                        placeholder={`Fortæl lidt om hvad du skal have lavet inden for ${titleText.toLowerCase()}...`}
                      />
                    </div>

                    {formStatus.error && (
                      <p className="text-red-400 text-xs font-semibold">{formStatus.error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus.submitting}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3.5 bg-white hover:bg-emerald-50 text-emerald-950 font-extrabold rounded-full transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 cursor-pointer uppercase tracking-wider text-[10px] sm:text-xs"
                    >
                      {formStatus.submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Indsender...
                        </>
                      ) : (
                        'Send forespørgsel'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </ScrollReveal>

          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8">
            
            {/* Other Services List */}
            {otherServices.length > 0 && (
              <ScrollReveal variant="fade-left" duration={800}>
                <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
                  <h3 className="text-base font-extrabold text-stone-900 mb-4 uppercase tracking-wider border-b border-stone-100 pb-3">
                    Andre ydelser
                  </h3>
                  <div className="space-y-2.5">
                    {otherServices.map((s) => {
                      const sTitle = typeof s.title === 'object' ? s.title.da : s.title;
                      const sSlug = typeof s.slug === 'object' ? s.slug.da : s.slug;
                      const SIcon = iconMap[s.icon_name] || HelpCircle;

                      return (
                        <Link
                          key={s.id}
                          to={`/ydelser/${sSlug}`}
                          className="flex items-center justify-between p-3.5 rounded-xl border border-stone-100 hover:border-emerald-500/20 hover:bg-emerald-50/20 transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-stone-600 group-hover:text-emerald-700 transition-colors">
                              <SIcon className="h-4 w-4" />
                            </div>
                            <span className="text-sm font-bold text-stone-700 group-hover:text-stone-900 transition-colors">
                              {sTitle}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-stone-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </ScrollReveal>
            )}

            {/* Quick Contact Box */}
            <ScrollReveal variant="fade-left" duration={800} delay={100}>
              <div className="bg-emerald-950 text-white rounded-2xl p-6 shadow-md relative overflow-hidden border border-emerald-900">
                {/* Decorative glow */}
                <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-emerald-800 rounded-full blur-2xl opacity-40 pointer-events-none" />
                
                <h3 className="text-base font-extrabold text-white mb-2 uppercase tracking-wider relative z-10">
                  Har du spørgsmål?
                </h3>
                <p className="text-white/80 text-xs mb-6 leading-relaxed relative z-10">
                  Du er altid velkommen til at ringe eller skrive for en uforpligtende snak eller rådgivning om dit haveprojekt.
                </p>

                <div className="space-y-3 relative z-10">
                  <a
                    href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
                  >
                    <div className="text-white">
                      <Phone className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-white/50">Ring direkte</span>
                      <span className="text-sm font-bold text-white">{contactInfo.phone}</span>
                    </div>
                  </a>

                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
                  >
                    <div className="text-white">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-white/50">Send e-mail</span>
                      <span className="text-sm font-bold text-white break-all">{contactInfo.email}</span>
                    </div>
                  </a>
                </div>
              </div>
            </ScrollReveal>

            {/* Link to Gallery */}
            <ScrollReveal variant="fade-left" duration={800} delay={200}>
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm text-center">
                <h4 className="font-extrabold text-stone-900 text-sm uppercase tracking-wider mb-2">Se vores tidligere arbejde</h4>
                <p className="text-stone-500 text-xs mb-4">Vi har udført mange spændende projekter i hele Midt- og Vestjylland. Se billederne i vores galleri.</p>
                <Link
                  to="/galleri"
                  className="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl transition-colors"
                >
                  Gå til galleri
                </Link>
              </div>
            </ScrollReveal>

          </div>

        </div>
      </div>

    </div>
  );
}
